import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Strategy } from '@/types/client';
import { strategiesApi } from '@/services/api';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import { formatDateTime } from '@/utils/formatters';
import PageCard, { DetailSection, DetailField } from '@/components/utils/PageCard';
import { Box, Tooltip } from '@mui/material';
import { PageFrame, Button, ActiveStatus, Note, Placeholder, confirmColors } from '@/components/ui';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import Swal from 'sweetalert2';

const StrategyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const strategyId = parseInt(id || '0');
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStrategy = useCallback(async () => {
    try {
      setLoading(true);
      const strategyData = await strategiesApi.getStrategy(strategyId);
      setStrategy(strategyData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch strategy details');
      console.error('Error fetching strategy:', err);
    } finally {
      setLoading(false);
    }
  }, [strategyId]);

  useEffect(() => {
    fetchStrategy();
  }, [fetchStrategy]);

  const handleActivate = async () => {
    if (!strategy) return;

    const result = await Swal.fire({
      title: 'Activate Strategy?',
      text: `Do you want to activate ${strategy.name}?`,
      icon: 'question',
      showCancelButton: true,
      ...confirmColors('success'),
      confirmButtonText: 'Yes, Activate',
    });
    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);
      await strategiesApi.activateStrategy(strategy.id);
      await fetchStrategy(); // Refresh data
      Swal.fire('Activated!', `${strategy.name} has been activated.`, 'success');
    } catch (err) {
      // Reported by the modal only — the inline Note is for load failures.
      console.error('Error activating strategy:', err);
      Swal.fire('Error', 'Failed to activate strategy.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!strategy) return;

    const result = await Swal.fire({
      title: 'Deactivate Strategy?',
      text: `Do you want to deactivate ${strategy.name}?`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, Deactivate',
    });
    if (!result.isConfirmed) return;

    try {
      setActionLoading(true);
      await strategiesApi.deactivateStrategy(strategy.id);
      await fetchStrategy(); // Refresh data
      Swal.fire('Deactivated!', `${strategy.name} has been deactivated.`, 'success');
    } catch (err) {
      // Reported by the modal only — the inline Note is for load failures.
      console.error('Error deactivating strategy:', err);
      Swal.fire('Error', 'Failed to deactivate strategy.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <PageFrame header={null}>
        <Placeholder kind="loading" />
      </PageFrame>
    );
  }

  if (error && !strategy) {
    return (
      <PageFrame header={null}>
        <Note
          tone="error"
          action={
            <Button size="sm" onClick={() => navigate('/strategies')}>
              Back to list
            </Button>
          }
        >
          {error}
        </Note>
      </PageFrame>
    );
  }

  if (!strategy) {
    return (
      <PageFrame header={null}>
        <Note tone="warning">Strategy not found.</Note>
      </PageFrame>
    );
  }

  const actions = (
    <>
      <Tooltip title="Back to list">
        <Button
          iconOnly
          onClick={() => navigate('/strategies')}
          aria-label="Back to list"
        >
          <ArrowBackIcon fontSize="small" />
        </Button>
      </Tooltip>

      {canWrite && (
        <Tooltip title="Edit">
          <span>
            <Button
              iconOnly
              onClick={() => navigate(`/strategies/${strategy.id}/edit`)}
              aria-label="Edit"
              disabled={actionLoading}
            >
              <EditIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
      )}

      {canWrite && (strategy.is_active ? (
        <Button
          variant="danger"
          icon={<BlockIcon fontSize="small" />}
          onClick={handleDeactivate}
          disabled={actionLoading}
          size="sm"
        >
          {actionLoading ? 'Processing…' : 'Deactivate'}
        </Button>
      ) : (
        <Button
          variant="success"
          icon={<CheckCircleIcon fontSize="small" />}
          onClick={handleActivate}
          disabled={actionLoading}
          size="sm"
        >
          {actionLoading ? 'Processing…' : 'Activate'}
        </Button>
      ))}
    </>
  );

  return (
    <PageCard
      title={`${strategy.code} — ${strategy.name}`}
      subtitle={<ActiveStatus active={!!strategy.is_active} />}
      actions={actions}
    >
      {error && (
        <Box sx={{ mb: 3 }}>
          <Note tone="error">{error}</Note>
        </Box>
      )}

      <DetailSection title="Basic Information">
        <DetailField label="Strategy Code" value={strategy.code} mono />
        <DetailField label="Strategy Name" value={strategy.name} />
        <DetailField label="Firm Type" value={strategy.firm_type_display} />
      </DetailSection>

      <DetailSection title="Audit Information">
        <DetailField label="Created Date" value={formatDateTime(strategy.created_ts)} />
        <DetailField label="Created By" value={strategy.created_by_name} />
        <DetailField
          label="Last Updated"
          value={strategy.updated_ts ? formatDateTime(strategy.updated_ts) : undefined}
        />
        <DetailField label="Updated By" value={strategy.updated_by_name} />
      </DetailSection>
    </PageCard>
  );
};

export default StrategyDetail;
